import React from "react";

import { CategoryData, HoverState, SelectionState } from "../types";

import "./styles.scss";

interface LegendProps {
  categories: CategoryData[];
  hoverState: HoverState;
  setHoverState: React.Dispatch<React.SetStateAction<HoverState>>;
  selectionState: SelectionState;
  setSelectionState: React.Dispatch<React.SetStateAction<SelectionState>>;
}

const Legend: React.FC<LegendProps> = ({
  categories,
  hoverState,
  setHoverState,
  selectionState,
  setSelectionState,
}) => {
  const handleCategoryHover = (categoryName: string) => {
    setHoverState({
      activeCategory: categoryName,
      activeLabel: null,
    });
  };

  const handleLabelHover = (categoryName: string, label: string) => {
    setHoverState({
      activeCategory: categoryName,
      activeLabel: label,
    });
  };

  const handleMouseLeave = () => {
    setHoverState({
      activeCategory: null,
      activeLabel: null,
    });
  };

  const handleCategoryClick = (categoryName: string) => {
    setSelectionState((prevState) => {
      const newSelectedCategories = new Set(prevState.selectedCategories);

      if (newSelectedCategories.has(categoryName)) {
        newSelectedCategories.delete(categoryName);
      } else {
        newSelectedCategories.add(categoryName);
      }

      if (
        newSelectedCategories.size === 0 &&
        prevState.selectedLabels.size === 0
      ) {
        return {
          selectedLabels: new Set(),
          selectedCategories: new Set(),
          hasSelection: false,
        };
      }

      return {
        ...prevState,
        selectedCategories: newSelectedCategories,
        hasSelection: true,
      };
    });
  };

  const handleLabelClick = (label: string) => {
    setSelectionState((prevState) => {
      const newSelectedLabels = new Set(prevState.selectedLabels);

      if (newSelectedLabels.has(label)) {
        newSelectedLabels.delete(label);
      } else {
        newSelectedLabels.add(label);
      }

      if (
        newSelectedLabels.size === 0 &&
        prevState.selectedCategories.size === 0
      ) {
        return {
          selectedLabels: new Set(),
          selectedCategories: new Set(),
          hasSelection: false,
        };
      }

      return {
        ...prevState,
        selectedLabels: newSelectedLabels,
        hasSelection: true,
      };
    });
  };

  const isLabelSelected = (label: string) => {
    return selectionState.selectedLabels.has(label);
  };

  const isCategorySelected = (categoryName: string) => {
    return selectionState.selectedCategories.has(categoryName);
  };

  const isItemActive = (categoryName: string, label: string) => {
    if (!selectionState.hasSelection) {
      return (
        (hoverState.activeLabel === label &&
          hoverState.activeCategory === categoryName) ||
        (hoverState.activeCategory === categoryName &&
          !hoverState.activeLabel) ||
        (!hoverState.activeCategory && !hoverState.activeLabel)
      );
    }

    return (
      isLabelSelected(label) ||
      (isCategorySelected(categoryName) && !selectionState.selectedLabels.size)
    );
  };

  return (
    <div className="interactive-line-chart-legend">
      {categories.map((category) => (
        <div key={category.categoryName} className="legend-category">
          <div
            className={`category-name ${
              isCategorySelected(category.categoryName) ? "selected" : ""
            }`}
            onMouseEnter={() => handleCategoryHover(category.categoryName)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleCategoryClick(category.categoryName)}
          >
            {category.categoryName}
          </div>
          <div className="legend-items">
            {category.items.map((item) => (
              <div
                key={item.key}
                className={`legend-item ${
                  isItemActive(category.categoryName, item.label)
                    ? "active"
                    : "inactive"
                } ${isLabelSelected(item.label) ? "selected" : ""}`}
                onMouseEnter={() =>
                  handleLabelHover(category.categoryName, item.label)
                }
                onMouseLeave={handleMouseLeave}
                onClick={() => handleLabelClick(item.label)}
              >
                <div
                  className="color-indicator"
                  style={{ backgroundColor: item.color || "#ccc" }}
                />
                <span className="label-text">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Legend;
